import React from 'react';
import PaymentMethodBlock from '../Billing/PaymentMethodSelection/PaymentMethodBlock';
import {
  ReviewSkeleton,
  ReviewTileHeader,
  PaymentMethodText,
  GiftCardCodeText,
  AddPaymentButton,
  PaymentMethodTextWrapper,
  ReviewTileWrapper
} from './styled';
import {
  findCustomerPaymentMethodInCartPaymentMethods,
  CartPaymentMethod,
  findGiftCardsInCartPaymentMethods,
  paypal
} from 'helpers/billing';
import { Show } from 'components/Functional';
import { useSelector } from 'react-redux';
import { totalSegmentsSelector, isPaypalEnabledSelector } from 'store/cart/selectors';
import { getGrandTotalFromTotalSegments } from 'store/cart/magentoSelectors';
import { isNotEmpty } from 'helpers/arrays';
import { Icon } from 'components';
import { PaymentMethodCode , PAYMENT_METHOD_AFTERPAY, CustomerPaymentMethod } from 'types/magento/CustomerPaymentMethod';
import { isAfterpayAppliedToCartSelector } from 'features/afterpay/store/selectors';
import { AfterpayMethodBlock } from 'features/afterpay/components/AfterpayBlock';
import useTrackComponentLoadTime from '../../analytics/itly/hooks/useTrackComponentLoadTime';
import { TrackingProp } from 'react-tracking';
import { customerEmailSelector } from 'store/customer/selectors';
import { ComponentName } from 'constants/componentName';

export interface ReviewPaymentTileProps {
  cartPaymentMethods: CartPaymentMethod[];
  isLoading: boolean;
  tracking: TrackingProp;
  cartId: number;
  handleChangeClick?: () => void;
  showSelectedMethod?: PaymentMethodCode;
}

const PaymentMethodChangeButton = ({ onClick }: { onClick?: () => void }) =>
  onClick ? <button onClick={onClick}>change payment method</button> : null;

const ReviewPaymentTile: React.FC<ReviewPaymentTileProps> = ({
  cartPaymentMethods,
  isLoading,
  tracking,
  cartId,
  handleChangeClick,
  showSelectedMethod
}) => {
  const customerPaymentMethod = findCustomerPaymentMethodInCartPaymentMethods(
    cartPaymentMethods
  ) as CustomerPaymentMethod;

  const email = useSelector(customerEmailSelector);

  useTrackComponentLoadTime({
    isLoading,
    componentName: ComponentName.PAYMENT_INFORMATION,
    tracking,
    cartId,
    email
  });

  const totalSegments = useSelector(totalSegmentsSelector);
  const requiresPayment = getGrandTotalFromTotalSegments(totalSegments) !== 0;
  const giftCards = findGiftCardsInCartPaymentMethods(cartPaymentMethods);
  const hasGiftCards = isNotEmpty(giftCards);
  const hasStoreCredit = isNotEmpty(cartPaymentMethods.filter((x: CartPaymentMethod) => x.type === 'storecredit'));
  const hasBraintreePayment = !!customerPaymentMethod.type;
  const isPaypalEnabled = useSelector(isPaypalEnabledSelector) || showSelectedMethod === paypal;
  const isAfterpay = useSelector(isAfterpayAppliedToCartSelector) || showSelectedMethod === PAYMENT_METHOD_AFTERPAY;

  const needsAddPayment = !hasBraintreePayment && !isAfterpay && !!handleChangeClick;

  return (
    <ReviewTileWrapper>
      <>
        <ReviewTileHeader secondary>Payment Information</ReviewTileHeader>

        <Show when={isLoading}>
          <ReviewSkeleton />
        </Show>

        <Show when={!isLoading}>
          <>
            <Show when={hasGiftCards}>
              <PaymentMethodTextWrapper>
                <PaymentMethodText secondary>Gift Card</PaymentMethodText>
                {giftCards.map((x, i) => (
                  <GiftCardCodeText secondary key={i}>
                    {x.code}
                  </GiftCardCodeText>
                ))}
              </PaymentMethodTextWrapper>
            </Show>

            <Show when={hasStoreCredit}>
              <PaymentMethodTextWrapper>
                <PaymentMethodText secondary>AU Credit Applied</PaymentMethodText>
              </PaymentMethodTextWrapper>
            </Show>

            <Show when={!requiresPayment}>
              <PaymentMethodText secondary>No payment required.</PaymentMethodText>
            </Show>

            <Show when={requiresPayment}>
              <>
                <Show when={isAfterpay}>
                  <>
                    <AfterpayMethodBlock />
                    <PaymentMethodChangeButton onClick={handleChangeClick} />
                  </>
                </Show>

                <Show when={isPaypalEnabled}>
                  <>
                    <PaymentMethodBlock paymentMethod={customerPaymentMethod} />
                    <PaymentMethodChangeButton onClick={handleChangeClick} />
                  </>
                </Show>

                <Show when={!isPaypalEnabled && !isAfterpay}>
                  <>
                    <Show when={hasBraintreePayment}>
                      <>
                        <PaymentMethodBlock paymentMethod={customerPaymentMethod} />
                        <PaymentMethodChangeButton onClick={handleChangeClick} />
                      </>
                    </Show>

                    <Show when={needsAddPayment}>
                      <AddPaymentButton role="button" onClick={handleChangeClick}>
                        <Icon icon="plus" /> Add Payment
                      </AddPaymentButton>
                    </Show>
                  </>
                </Show>
              </>
            </Show>
          </>
        </Show>
      </>
    </ReviewTileWrapper>
  );
};

export default ReviewPaymentTile;
